import ReplayIcon from '@mui/icons-material/Replay';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import TryIcon from '@mui/icons-material/Try';
import { Paper, Stack, Typography } from '@mui/material';
import { formatRelative } from 'date-fns';
import { useAiPauseSmartReply } from '../../../../sic-lib/api/hooks/useAiSmartReply';
import { AiSmartReplyContactPlanItemStatus } from '../../../../sic-lib/enums/AiSmartReplyContactPlanItemStatus';
import { AiSmartReplyContactPlanState } from '../../../../sic-lib/types/Ai';
import { UserInfo } from '../../../../sic-lib/types/UserConfig';
import { aiSmartReplyContactPlanItemStatusToLabel } from '../../../../sic-lib/utils/aiHelpers';
import UserChip from '../../../Settings/Users/UserChip';
import AxiosErrorAlert from '../../../Shared/Alerts/AxiosErrorAlert';
import ButtonLoading from '../../../Shared/ButtonLoading';

interface Props {
    plan: AiSmartReplyContactPlanState;
    users: UserInfo[];
    isAdmin: boolean;
    userName: string;
}

export default function SmartReplyDialogItem({ plan, users, isAdmin, userName }: Props) {
    const {
        mutate: handlePauseSmartReply,
        isPending: isPausingSmartReply,
        isSuccess: isPausingSmartReplySuccess,
        isError: isPausingSmartReplyError,
        error: pausingError,
    } = useAiPauseSmartReply();

    return (
        <Paper variant="outlined" sx={{ p: 1 }}>
            <Stack direction="column" gap={1}>
                <Stack
                    key={plan.planId}
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexGrow={1}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Stack direction="column" alignItems="flex-start">
                            <Typography variant="subtitle1">{plan.planName}</Typography>
                            <UserChip users={users} userName={plan.userName} />
                        </Stack>
                    </Stack>
                    <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                        alignItems="center"
                    >
                        <Stack direction="column">
                            <Typography variant="body1" align="right">
                                {aiSmartReplyContactPlanItemStatusToLabel(plan.planItemStatus)}
                            </Typography>
                            {plan.dateScheduled && (
                                <Typography variant="body2" align="right">
                                    {plan.planItemStatus ===
                                    AiSmartReplyContactPlanItemStatus.Scheduled
                                        ? formatRelative(new Date(plan.dateScheduled), new Date())
                                        : undefined}
                                </Typography>
                            )}
                        </Stack>
                        {plan.planItemStatus === AiSmartReplyContactPlanItemStatus.Scheduled && (
                            <ScheduleSendIcon fontSize="medium" />
                        )}
                        {plan.planItemStatus === AiSmartReplyContactPlanItemStatus.Sending && (
                            <RocketLaunchIcon fontSize="medium" />
                        )}
                        {plan.planItemStatus === AiSmartReplyContactPlanItemStatus.Unscheduled && (
                            <TryIcon fontSize="medium" />
                        )}
                        {plan.planItemStatus === AiSmartReplyContactPlanItemStatus.Failed && (
                            <ReplayIcon fontSize="medium" />
                        )}
                    </Stack>
                </Stack>
                {(isAdmin || plan.userName === userName) && (
                    <Stack direction="column" justifyContent="center" alignItems="center" gap={1}>
                        <ButtonLoading
                            variant="text"
                            color="error"
                            isLoading={isPausingSmartReply}
                            isSuccess={isPausingSmartReplySuccess}
                            isError={isPausingSmartReplyError}
                            successLabel="Plan"
                            size="small"
                            onClick={() => {
                                handlePauseSmartReply({
                                    contactId: plan.contactId,
                                    contactPlanId: plan.contactPlanId,
                                });
                            }}
                            // fullWidth
                        >
                            Stop Plan
                        </ButtonLoading>
                        <AxiosErrorAlert error={pausingError} />
                    </Stack>
                )}
            </Stack>
        </Paper>
    );
}
